import { useEffect, useState } from 'react';
import { graphql } from './index';
import { ToastType, useToast } from 'services/toast';
import { useQuery as useQueryV2 } from '@tanstack/react-query';
import { isNumber } from 'utils/validation';

type QueryOptions<T> = {
  usePagination?: boolean;
  onError?: (error: any) => void;
  onFetchSuccess?: (data: T) => void;
  preventInitialFetch?: boolean;
};

/**
 * @deprecated use apollo client instead
 */
const useQuery = <T>(
  queryFn: (params: any) => Promise<any>,
  params: Record<string, unknown> = {},
  {
    usePagination,
    onError,
    onFetchSuccess,
    preventInitialFetch
  }: QueryOptions<T> = {
    usePagination: false
  }
) => {
  // @ts-expect-error: //
  const [data, setData] = useState<T | null>(usePagination ? [] : null);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(
    preventInitialFetch ? false : true
  );
  const [isError, setIsError] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const { showToast } = useToast();

  const executeQuery = async (
    additionalParams: Record<string, unknown> = {}
  ) => {
    const shouldUseLoadingPagination =
      usePagination && Array.isArray(data) && data?.length;

    if (shouldUseLoadingPagination) {
      setIsNextPageLoading(true);
    }

    try {
      if (!isLoading) {
        setIsLoading(true);
      }

      const result = await queryFn({ ...params, ...additionalParams });
      if (usePagination) {
        if (Array.isArray(result.items)) {
          // @ts-expect-error: we are checking if data and result.items are arrays
          setData([...data, ...result.items]);
        }

        setNextToken(result.nextToken);
      } else {
        setData(result);
      }

      onFetchSuccess?.(result);
      setIsError(false);
    } catch (error: any) {
      onError?.(error);
      setIsError(true);

      if (error?.errors?.[0]?.errorType === 'Unauthorized') {
        showToast({ message: 'You are not authorized', type: ToastType.Error });
      }
    } finally {
      setIsLoading(false);
      if (shouldUseLoadingPagination) {
        setIsNextPageLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!preventInitialFetch) {
      const allParamsDefined = Object.values(params).every((param) => !!param);
      if (allParamsDefined) {
        executeQuery(params);
      }
    }
  }, [JSON.stringify(params), preventInitialFetch]);

  const loadNextPage = async () => {
    const nextTokenToUse = params?.nextToken || nextToken;

    if (usePagination && nextTokenToUse) {
      await executeQuery({ nextToken: nextTokenToUse });
    }
  };

  const refetch = async (extraParams: Record<string, unknown> = {}) => {
    const allParamsDefined = Object.values(params).every((param) => !!param);
    if (allParamsDefined) {
      await executeQuery({ ...params, ...extraParams });
    }
  };

  return {
    data,
    isLoading,
    isError,
    loadNextPage,
    isNextPageLoading,
    nextToken,
    refetch
  };
};

type Param = {
  subQuery: string;
  subParams: { [key: string]: any } | Partial<Record<string, any>>;
  onFetchSuccess?: (data: any) => void;
};

function isFilterQuery(obj: any): boolean {
  for (const key in obj) {
    if (obj[key] && typeof obj[key] === 'object' && 'eq' in obj[key]) {
      return true;
    }
  }
  return false;
}

export const useSubscriber = ({
  subQuery,
  subParams,
  onFetchSuccess
}: Param): { data: any; error: any } => {
  const [data, setData] = useState(null);
  const [error, setError] = useState<Error | undefined>();

  const variables = isFilterQuery(subParams)
    ? { filter: subParams }
    : subParams;

  useEffect(() => {
    const subscription = graphql.subscribe(subQuery, variables, {
      next: ({ value }) => {
        if (value?.data) {
          setData(value.data);
          onFetchSuccess?.(value.data);
        } else {
          setError(value);
        }
      },
      error: (error) => {
        setError(error as Error);
      }
    });

    return () => subscription.unsubscribe();
  }, [JSON.stringify(subParams)]);

  return { data, error };
};

export type QueryV2Params = {
  query: string;
  params: { [key: string]: any } | Partial<Record<string, any>>;
  onSubscribe?: (data: any) => void;
  onError?: (error: any) => void;
};

export type SubscriberV2Params = {
  queries: QueryV2Params[];
  disableOnEvents?: boolean;
};

export const useSubscriberV2 = ({
  queries,
  disableOnEvents
}: SubscriberV2Params): { data: any; error: any } => {
  const [data, setData] = useState(null);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const subscriptions = queries.map(
      ({ query, params, onSubscribe, onError }) => {
        const variables = isFilterQuery(params) ? { filter: params } : params;

        return graphql.subscribe(query, variables, {
          next: ({ value }) => {
            if (!value?.data) {
              setError(value);
              return;
            }
            setData(value.data);
            !disableOnEvents && onSubscribe?.(value.data);
          },
          error: (error) => {
            setError(error as Error);
            !disableOnEvents && onError?.(error);
          }
        });
      }
    );

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, []);

  return { data, error };
};

type MutationOptions<T> = {
  onError?: (error: Error) => void;
  onSuccess?: (data: T) => void;
  errorTextMessage?: string;
  successTextMessage?: string;
  disableSuccessToast?: boolean;
};

/**
 * @deprecated use apollo client instead
 */
const useMutation = <T, P = Record<string, unknown>>(
  mutation: (params: P) => Promise<T>,
  options?: MutationOptions<T>
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const mutate = async (params?: P) => {
    setIsLoading(true);
    setError('');

    try {
      const response: T = await mutation(params as P);
      options?.onSuccess?.(response);

      if (!options?.disableSuccessToast)
        showToast({
          message: options?.successTextMessage || 'Success',
          type: ToastType.Success
        });

      setError('');
      return response;
    } catch (error: any) {
      options?.onError?.(error);
      setError(error?.message);
      showToast({
        message: options?.errorTextMessage || 'Something went wrong',
        type: ToastType.Error
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate,
    isLoading,
    error
  };
};

/**
 * @deprecated use apollo client instead
 */
const usePaginatedQuery = <T>({
  queryFn,
  variables = {},
  limit = 10,
  onFetchSuccess
}: {
  queryFn: (params: {
    nextToken: string | null;
    limit: number;
    variables: any;
  }) => Promise<{ items: T[]; nextToken: string; total: number }>;
  variables: any;
  limit: number;
  onFetchSuccess?: (data: {
    items: T[];
    nextToken: string;
    total: number;
  }) => void;
}) => {
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchItems = async (pageToken: string | null) => {
    const params = {
      variables,
      nextToken: pageToken,
      limit
    };

    const response = await queryFn(params);
    return response;
  };

  const { data, isLoading, isFetching, refetch } = useQueryV2<{
    items: T[];
    nextToken: string;
    total: number;
  }>({
    queryKey: ['paginatedItems', nextToken, limit, variables],
    queryFn: () => fetchItems(nextToken)
  });

  const handleNext = () => {
    if (data?.nextToken) {
      setNextToken(data.nextToken);
    }

    if (nextToken) {
      setPrevTokens([...prevTokens, nextToken]);
    }
  };

  const handlePrevious = () => {
    const tokens = [...prevTokens];
    const previousToken = tokens.pop();

    setPrevTokens(tokens);
    setNextToken(previousToken || null);
  };

  useEffect(() => {
    if (isNumber(data?.total)) {
      setTotalItems(data?.total || 0);
    }
  }, [data?.total]);

  useEffect(() => {
    if (data?.items) {
      onFetchSuccess?.(data);
    }
  }, [JSON.stringify(data?.items)]);

  useEffect(() => {
    setNextToken(null);
    setPrevTokens([]);

    refetch();
  }, [JSON.stringify(variables)]);

  useEffect(() => {
    refetch();
  }, [limit]);

  return {
    items: data?.items || [],
    isLoading,
    handleNext,
    handlePrevious,
    totalItems,
    isFetching,
    refetch
  };
};

export { useQuery, useMutation, usePaginatedQuery };
